// Compact "contact support" card — shown on the forgot-password modal, login screen, etc.
// Reads /api/support-contacts (public); renders only channels the admin has configured.
import { useEffect, useState } from "react";
import api from "@/api/client";
import { MessageCircle, Phone, Mail, Clock, HelpCircle } from "lucide-react";

// Simple in-module cache so we don't refetch on every mount.
let cachedContacts = null;
let inflight = null;

function loadContacts() {
  if (cachedContacts) return Promise.resolve(cachedContacts);
  if (inflight) return inflight;
  inflight = api.get("/support-contacts")
    .then((r) => { cachedContacts = r.data; return r.data; })
    .catch(() => ({}))
    .finally(() => { inflight = null; });
  return inflight;
}

export default function SupportContactsCard({ variant = "default", title = "تواصل مع إدارة طلبية", subtitle }) {
  const [c, setC] = useState(null);
  useEffect(() => { loadContacts().then(setC); }, []);
  if (!c) return null;
  const hasAny = c.whatsapp || c.phone || c.email;
  if (!hasAny && !c.note) return null;

  const compact = variant === "compact";
  const waNumber = (c.whatsapp || "").replace(/\D/g, "");

  return (
    <div
      className={`rounded-2xl border ${compact ? "p-3" : "p-4"} bg-gradient-to-l from-blue-50 to-white border-primary-brand/20`}
      data-testid="support-contacts-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-primary-brand text-white flex items-center justify-center shrink-0">
          <HelpCircle size={14}/>
        </div>
        <div className="flex-1">
          <div className="font-black text-sm" style={{fontFamily:"Cairo"}}>{title}</div>
          {subtitle && <div className="text-[10px] text-[var(--text-secondary)] leading-relaxed">{subtitle}</div>}
        </div>
      </div>

      <div className="space-y-1.5">
        {c.whatsapp && (
          <a
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white border border-[var(--border)] hover:border-green-500 hover:bg-green-50 transition-colors group"
            data-testid="support-whatsapp"
          >
            <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 group-hover:bg-green-500 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
              <MessageCircle size={14}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-[var(--text-secondary)]">واتساب</div>
              <div className="font-mono text-sm font-bold text-[var(--text-primary)]" dir="ltr">{c.whatsapp}</div>
            </div>
            <span className="text-[10px] font-black text-green-600 shrink-0">فتح ›</span>
          </a>
        )}
        {c.phone && (
          <a
            href={`tel:${c.phone}`}
            className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white border border-[var(--border)] hover:border-primary-brand hover:bg-blue-50 transition-colors group"
            data-testid="support-phone"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 text-primary-brand group-hover:bg-primary-brand group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
              <Phone size={14}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-[var(--text-secondary)]">اتصال هاتفي</div>
              <div className="font-mono text-sm font-bold text-[var(--text-primary)]" dir="ltr">{c.phone}</div>
            </div>
            <span className="text-[10px] font-black text-primary-brand shrink-0">اتصال ›</span>
          </a>
        )}
        {c.email && (
          <a
            href={`mailto:${c.email}`}
            className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white border border-[var(--border)] hover:border-blue-500 hover:bg-blue-50 transition-colors group"
            data-testid="support-email"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
              <Mail size={14}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-[var(--text-secondary)]">البريد الإلكتروني</div>
              <div className="text-sm font-bold text-[var(--text-primary)] truncate" dir="ltr">{c.email}</div>
            </div>
          </a>
        )}
      </div>

      {(c.hours || c.note) && (
        <div className="mt-2 pt-2 border-t border-primary-brand/10 space-y-1 text-[10px] text-[var(--text-secondary)] leading-relaxed">
          {c.hours && (
            <div className="flex items-center gap-1">
              <Clock size={10}/> ساعات الدعم: <b className="text-[var(--text-primary)]">{c.hours}</b>
            </div>
          )}
          {c.note && <div>{c.note}</div>}
        </div>
      )}

      {!hasAny && (
        <div className="text-xs text-[var(--text-secondary)] text-center py-2">
          لم يتم إضافة قنوات تواصل بعد. راجع الإدارة.
        </div>
      )}
    </div>
  );
}

export function invalidateSupportContactsCache() {
  cachedContacts = null;
}
